import {CustomState} from '@mintdata/mint-components/components/image/model/CustomState';
import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {Background} from '@mintdata/mint-kit/api/models/background/Background';
import {BackgroundSize} from '@mintdata/mint-kit/api/models/BackgroundSize';
import {BaseComponentModel} from '@mintdata/mint-kit/api/models/BaseComponentModel';
import {Border} from '@mintdata/mint-kit/api/models/Border';
import {BoxShadow} from '@mintdata/mint-kit/api/models/BoxShadow';
import {ColorWithVisibility} from '@mintdata/mint-kit/api/models/ColorWithVisibility';
import {PositiveOffsets} from '@mintdata/mint-kit/api/models/PositiveOffsets';
import {TextAlign} from '@mintdata/mint-kit/api/models/TextAlign';
import {TextVerticalAlign} from '@mintdata/mint-kit/api/models/TextVerticalAlign';
import {FieldPath} from '@mintdata/mint-kit/api/properties/BindPath';
import {PropertyCategory} from '@mintdata/mint-kit/api/properties/PropertyCategory';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AStringConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AStringConfig';
import {BackgroundSizeConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/BackgroundSizeConfig';
import {BeanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/BeanConfig';
import {PositiveOffsetsConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/PositiveOffsetsConfig';
import {TextAlignConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/TextAlignConfig';
import {TextVerticalAlignConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/TextVerticalAlignConfig';

export class ImageModel extends BaseComponentModel
{
    @Property<AString>({
        title: 'Url',
        tip: 'Url',
        isRequired: false,
        typeConfig: AStringConfig,
        typeCtor: AString
    })
    public url:AString;

    @Property<ColorWithVisibility>({
        title: 'Color1',
        tip: 'Color1',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: ColorWithVisibility
    })
    public color1:ColorWithVisibility;

    @Property<ColorWithVisibility>({
        title: 'Color2',
        tip: 'Color2',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: ColorWithVisibility
    })
    public color2:ColorWithVisibility;

    @Property<BackgroundSize>({
        title: 'Fill',
        tip: 'Fill',
        isRequired: false,
        typeConfig: BackgroundSizeConfig,
        typeCtor: BackgroundSize
    })
    public backgroundSize:BackgroundSize;

    @Property<TextAlign>({
        title: 'Horizontal Align',
        tip: 'Horizontal Align',
        isRequired: false,
        typeConfig: TextAlignConfig,
        typeCtor: TextAlign
    })
    public horizontalAlign:TextAlign;

    @Property<TextVerticalAlign>({
        title: 'Vertical Align',
        tip: 'Vertical Align',
        isRequired: false,
        typeConfig: TextVerticalAlignConfig,
        typeCtor: TextVerticalAlign
    })
    public verticalAlign:TextVerticalAlign;

    @Property<Background>({
        title: 'Background',
        tip: 'Background',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: Background
    })
    public background:Background;

    @Property<Border>({
        title: 'Border',
        tip: 'Border',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: Border
    })
    public border:Border;

    @Property<BoxShadow>({
        title: 'Shadows',
        tip: 'Shadows',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: BoxShadow
    })
    public boxShadow:BoxShadow;

    @Property<PositiveOffsets>({
        title: 'Padding',
        tip: 'Padding',
        category: PropertyCategory.Layout,
        isRequired: false,
        typeConfig: PositiveOffsetsConfig,
        typeCtor: PositiveOffsets
    })
    public padding:PositiveOffsets;

    @Property<CustomState>({
        title: 'Hover State',
        tip: 'Hover State',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: CustomState
    })
    public hoverState:CustomState;

    @Property<CustomState>({
        title: 'Active State',
        tip: 'Active State',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: CustomState
    })
    public activeState:CustomState;

    public constructor(url:AString,
                       color1:ColorWithVisibility,
                       color2:ColorWithVisibility,
                       backgroundSize:BackgroundSize,
                       horizontalAlign:TextAlign,
                       verticalAlign:TextVerticalAlign,
                       background:Background,
                       border:Border,
                       boxShadow:BoxShadow,
                       padding:PositiveOffsets,
                       hoverState:CustomState,
                       activeState:CustomState)
    {
        super();

        this.url = url;
        this.color1 = color1;
        this.color2 = color2;
        this.backgroundSize = backgroundSize;
        this.horizontalAlign = horizontalAlign;
        this.verticalAlign = verticalAlign;
        this.background = background;
        this.border = border;
        this.boxShadow = boxShadow;
        this.padding = padding;
        this.hoverState = hoverState;
        this.activeState = activeState;
    }

    public static default():ImageModel
    {
        return new ImageModel(
            AString.fromString('drawings2:photo'), //https://s3-us-west-2.amazonaws.com/bdp-static/Placeholder.png
            ColorWithVisibility.fromHexAlpha('#444f5c', 1),
            ColorWithVisibility.fromHexAlpha('#3f3d56', 1),
            BackgroundSize.cover(),
            TextAlign.center(),
            TextVerticalAlign.middle(),
            Background.default(),
            Border.default(),
            BoxShadow.none(),
            PositiveOffsets.fromEqualPixels(0),
            CustomState.hover(),
            CustomState.active()
        );
    }

    public static normalToHoverStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['url', 'hoverState.url'],
            ['color1', 'hoverState.color1'],
            ['color2', 'hoverState.color2'],
            ['backgroundSize', 'hoverState.backgroundSize'],
            ['horizontalAlign', 'hoverState.horizontalAlign'],
            ['verticalAlign', 'hoverState.verticalAlign'],
            ['background', 'hoverState.background'],
            ['border', 'hoverState.border'],
            ['boxShadow', 'hoverState.boxShadow']
        ]);
    }

    public static normalToActiveStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['url', 'activeState.url'],
            ['color1', 'activeState.color1'],
            ['color2', 'activeState.color2'],
            ['backgroundSize', 'activeState.backgroundSize'],
            ['horizontalAlign', 'activeState.horizontalAlign'],
            ['verticalAlign', 'activeState.verticalAlign'],
            ['background', 'activeState.background'],
            ['border', 'activeState.border'],
            ['boxShadow', 'activeState.boxShadow']
        ]);
    }

    public static hoverToActiveStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['hoverState.url', 'activeState.url'],
            ['hoverState.color2', 'activeState.color2'],
            ['hoverState.color2', 'activeState.color2'],
            ['hoverState.backgroundSize', 'activeState.backgroundSize'],
            ['hoverState.horizontalAlign', 'activeState.horizontalAlign'],
            ['hoverState.verticalAlign', 'activeState.verticalAlign'],
            ['hoverState.background', 'activeState.background'],
            ['hoverState.border', 'activeState.border'],
            ['hoverState.boxShadow', 'activeState.boxShadow']
        ]);
    }
}
